import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {TranslatePipe} from '@ngx-translate/core';

export type DropzoneVariant = 'pdf' | 'image';

@Component({
  selector: 'app-file-dropzone',
  standalone: true,
  imports: [MatIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './file-dropzone.component.html',
  styleUrl: './file-dropzone.component.scss',
})
export class FileDropzoneComponent {
  @Input() variant: DropzoneVariant = 'pdf';
  @Input() accept = '';
  @Input() maxBytes = 0;
  @Output() readonly fileSelected = new EventEmitter<File>();
  @Output() readonly fileRejected = new EventEmitter<string>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly cdr = inject(ChangeDetectorRef);

  isDragging = false;

  get iconName(): string {
    return this.variant === 'pdf' ? 'cloud_upload' : 'image';
  }

  get dropReleaseKey(): string {
    return this.variant === 'pdf' ? 'together.drop.releasePdf' : 'together.drop.releaseImage';
  }

  get dropLabelKey(): string {
    return this.variant === 'pdf' ? 'together.drop.pdf' : 'together.drop.image';
  }

  get limitsKey(): string {
    return this.variant === 'pdf' ? 'together.drop.pdfLimits' : 'together.drop.imageLimits';
  }

  openDialog(): void {
    this.fileInput.nativeElement.click();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openDialog();
    }
  }

  onDragover(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.cdr.markForCheck();
  }

  onDragleave(): void {
    this.isDragging = false;
    this.cdr.markForCheck();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    this.cdr.markForCheck();
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.validateAndEmit(file);
    }
  }

  onFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.validateAndEmit(file);
    }
    (event.target as HTMLInputElement).value = '';
  }

  private validateAndEmit(file: File): void {
    if (this.variant === 'image') {
      if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
        this.fileRejected.emit('together.validation.imageType');
        return;
      }
      if (file.size === 0) {
        this.fileRejected.emit('together.validation.imageEmpty');
        return;
      }
      if (file.size > this.maxBytes) {
        this.fileRejected.emit('together.validation.imageTooLarge');
        return;
      }
    } else {
      if (file.type !== 'application/pdf') {
        this.fileRejected.emit('together.validation.fileType');
        return;
      }
      if (file.size === 0) {
        this.fileRejected.emit('together.validation.fileEmpty');
        return;
      }
      if (file.size > this.maxBytes) {
        this.fileRejected.emit('together.validation.fileTooLarge');
        return;
      }
    }
    this.fileSelected.emit(file);
  }
}
